<template>
  <el-dialog
    :visible.sync="dialogVisible"
    title="榜单数据管理"
    width="80%"
    :before-close="handleClose"
  >
    <div class="rank-dialog-content">
      <!-- 榜单组件选择 -->
      <div class="rank-selector">
        <el-form :inline="true">
          <el-form-item label="选择榜单组件:">
            <el-select v-model="selectedUnitId" placeholder="请选择榜单组件" @change="handleUnitChange">
              <el-option 
                v-for="unit in rankUnits" 
                :key="unit.unitId" 
                :label="unit.unitAka || unit.unitName" 
                :value="unit.unitId">
                <span>{{ unit.unitAka }}</span>
                <span style="float: right; color: #8492a6; font-size: 13px">{{ unit.unitName }}</span>
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="显示TOP:">
            <el-input-number v-model="topCount" :min="1" :max="100" placeholder="TOP数量"></el-input-number>
          </el-form-item>
          <el-form-item label="日期:">
            <el-date-picker
              v-model="rankDate"
              type="date"
              placeholder="选择日期"
              value-format="yyyy-MM-dd"
              :clearable="true">
            </el-date-picker>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="fetchRankData">查询数据</el-button>
            <el-button type="success" @click="exportRankData">导出数据</el-button>
          </el-form-item>
        </el-form>
      </div>

      <!-- 操作区域 -->
      <div class="operation-area" v-if="selectedUnitId">
        <el-alert
          title="危险操作区域"
          type="warning"
          :closable="false"
          show-icon>
        </el-alert>
        <div class="operation-buttons">
          <el-form :inline="true">
            <el-form-item label="移除用户:">
              <el-input v-model="removeUserId" placeholder="请输入用户ID" auto-complete="off" autocomplete="off"></el-input>
            </el-form-item>
            <el-form-item>
              <el-button type="warning" @click="removeRankUser">移除用户</el-button>
            </el-form-item>
            <el-form-item label="结算密钥:">
              <el-input v-model="settlementKey" placeholder="请输入结算密钥" show-password auto-complete="off" :autocomplete="'new-password'"></el-input>
            </el-form-item>
            <el-form-item>
              <el-button type="danger" @click="handleRankSettlement">榜单结算</el-button>
            </el-form-item>
          </el-form>
        </div>
      </div>

      <!-- 榜单数据表格 -->
      <div class="rank-table" v-if="rankData.length > 0">
        <div class="rank-table-header">
          <div class="table-info">
            <el-alert 
              :title="`共 ${rankData.length} 条记录`" 
              type="info" 
              :closable="false"
              show-icon>
            </el-alert>
          </div>
          <div class="table-actions">
            <el-input 
              v-model="userSearchText" 
              placeholder="搜索用户UID/RID..." 
              size="small" 
              prefix-icon="el-icon-search"
              clearable
              style="width: 200px;">
            </el-input>
          </div>
        </div>
        
        <el-table 
          :data="paginatedRankData" 
          border 
          stripe
          height="400"
          :row-class-name="getRankRowClassName">
          <el-table-column prop="rankNum" label="排名" width="80" align="center">
            <template #default="{ row }">
              <el-tag 
                :type="getRankTagType(row.rankNum)" 
                effect="dark">
                {{ row.rankNum }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="rid" label="用户RID" width="120" align="center">
            <template #default="{ row }">
              <el-link type="primary" @click="copyToClipboard(row.rid.toString())">
                {{ row.rid }}
              </el-link>
            </template>
          </el-table-column>
          <el-table-column prop="uid" label="用户UID" width="200" align="center" show-overflow-tooltip>
            <template #default="{ row }">
              <el-link type="primary" @click="copyToClipboard(row.uid)">
                {{ row.uid }}
              </el-link>
            </template>
          </el-table-column>
          <el-table-column prop="rankCount" label="榜单积分" width="120" align="center" sortable>
            <template #default="{ row }">
              <el-tag type="success">{{ formatScore(row.rankCount) }}</el-tag>
            </template>
          </el-table-column>
          <!-- <el-table-column prop="doubleCount" label="额外积分" width="120" align="center" sortable>
            <template #default="{ row }">
              <el-tag v-if="row.doubleCount > 0" type="warning">{{ formatScore(row.doubleCount) }}</el-tag>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column label="总积分" width="120" align="center">
            <template #default="{ row }">
              <el-tag type="primary">{{ formatScore((row.rankCount || 0) + (row.doubleCount || 0)) }}</el-tag>
            </template>
          </el-table-column> -->
          <el-table-column label="操作" width="100" align="center">
            <template #default="{ row }">
              <el-button size="mini" type="danger" @click="confirmRemoveUser(row)">移除</el-button>
            </template>
          </el-table-column>
        </el-table>
        
        <!-- 分页 -->
        <div class="rank-pagination">
          <el-pagination
            background
            layout="prev, pager, next, sizes, total"
            :current-page="rankCurrentPage"
            :page-size="rankPageSize"
            :page-sizes="[10, 20, 50, 100]"
            :total="filteredRankData.length"
            @current-change="handleRankPageChange"
            @size-change="handleRankSizeChange">
          </el-pagination>
        </div>
      </div>

      <div v-else-if="!loading" class="empty-data">
        <el-empty description="暂无榜单数据"></el-empty>
      </div>

      <div v-if="loading" class="loading-area">
        <div class="loading-content">
          <i class="el-icon-loading"></i>
          <p>加载中...</p>
        </div>
      </div>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">关闭</el-button>
      <el-button type="primary" @click="fetchRankData" v-if="selectedUnitId">刷新数据</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { 
  getUnitRankDataReq, 
  exportUnitRankDataReq, 
  unitRankSettlementReq, 
  removeUnitRankUserReq 
} from '@/api/activityUnitRequest'

export default {
  name: 'UnitRankDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    eventData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      dialogVisible: false,
      loading: false,
      selectedUnitId: null,
      topCount: 10,
      rankDate: null, // 日期，格式 yyyy-MM-dd
      removeUserId: '',
      settlementKey: '',
      rankData: [],
      rankUnits: [],
      // 榜单分页相关
      userSearchText: '',
      rankCurrentPage: 1,
      rankPageSize: 20
    }
  },
  computed: {
    filteredRankData() {
      if (!this.userSearchText) return this.rankData
      
      const searchText = this.userSearchText.toLowerCase()
      return this.rankData.filter(item => 
        item.uid.toLowerCase().includes(searchText) ||
        item.rid.toString().includes(searchText)
      )
    },
    paginatedRankData() {
      const start = (this.rankCurrentPage - 1) * this.rankPageSize
      const end = start + this.rankPageSize
      return this.filteredRankData.slice(start, end)
    }
  },
  watch: {
    value(val) {
      this.dialogVisible = val
      if (val && this.eventData) {
        this.initRankUnits()
      }
    },
    dialogVisible(val) {
      this.$emit('input', val)
      if (!val) {
        this.resetData()
      }
    }
  },
  methods: {
    initRankUnits() {
      // 从活动数据中提取榜单组件
      if (this.eventData && this.eventData.units) {
        this.rankUnits = this.eventData.units.filter(unit => unit.unitType === 4) // 榜单组件类型为4
        if (this.rankUnits.length > 0) {
          this.selectedUnitId = this.rankUnits[0].unitId
        }
      }
    },
    handleUnitChange() {
      this.rankData = []
      this.userSearchText = ''
      this.rankCurrentPage = 1
    },
    // 分页相关方法
    handleRankPageChange(page) {
      this.rankCurrentPage = page
    },
    handleRankSizeChange(size) {
      this.rankPageSize = size
      this.rankCurrentPage = 1
    },
    // 格式化积分显示
    formatScore(score) {
      if (!score || score === 0) return '0'
      if (score < 1000) return score.toString()
      if (score < 1000000) return (score / 1000).toFixed(1) + 'K'
      return (score / 1000000).toFixed(1) + 'M'
    },
    // 获取排名标签类型
    getRankTagType(rank) {
      if (rank === 1) return 'danger'
      if (rank <= 3) return 'warning'
      if (rank <= 10) return 'success'
      return 'info'
    },
    // 获取榜单行样式
    getRankRowClassName({ row, rowIndex }) {
      if (row.rankNum <= 3) return 'top-rank'
      return ''
    },
    // 复制到剪贴板
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$message.success('已复制到剪贴板')
      }).catch(() => {
        this.$message.error('复制失败')
      })
    },
    async fetchRankData() {
      if (!this.selectedUnitId) {
        this.$message.warning('请先选择榜单组件')
        return
      }

      this.loading = true
      try {
        const params = {
          eventCode: this.eventData.eventCode,
          unitId: this.selectedUnitId,
          top: this.topCount
        }
        
        // date 是可选参数，如果有值才传
        if (this.rankDate) {
          params.date = this.rankDate
        }
        
        const res = await getUnitRankDataReq(params)

        if (res.code === 0) {
          this.rankData = res.data || []
          this.$message.success('榜单数据加载成功')
        } else {
          this.$message.error(res.msg)
          this.rankData = []
        }
      } catch (error) {
        this.$message.error('获取榜单数据失败')
        this.rankData = []
      } finally {
        this.loading = false
      }
    },
    async exportRankData() {
      if (!this.selectedUnitId) {
        this.$message.warning('请先选择榜单组件')
        return
      }

      try {
        const params = {
          eventCode: this.eventData.eventCode,
          unitId: this.selectedUnitId,
          top: this.topCount
        }
        
        // date 是可选参数，如果有值才传
        if (this.rankDate) {
          params.date = this.rankDate
        }
        
        const res = await exportUnitRankDataReq(params)

        // 创建下载链接
        const blob = new Blob([res], { type: 'application/vnd.ms-excel' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `榜单数据_${this.eventData.eventCode}_${this.selectedUnitId}.xlsx`
        link.click()
        window.URL.revokeObjectURL(url)

        this.$message.success('榜单数据导出成功')
      } catch (error) {
        this.$message.error('导出榜单数据失败')
      }
    },
    confirmRemoveUser(row) {
      this.$confirm(`确定要移除用户 ${row.uid} (RID: ${row.rid}) 的榜单数据吗？`, '确认移除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.removeUserFromRank(row.uid)
      })
    },
    async removeRankUser() {
      if (!this.removeUserId.trim()) {
        this.$message.warning('请输入要移除的用户ID')
        return
      }
      await this.removeUserFromRank(this.removeUserId.trim())
    },
    async removeUserFromRank(uid) {
      if (!this.selectedUnitId) {
        this.$message.warning('请先选择榜单组件')
        return
      }

      try {
        const params = {
          eventCode: this.eventData.eventCode,
          unitId: this.selectedUnitId,
          uid: uid
        }
        
        // date 是可选参数，如果有值才传
        if (this.rankDate) {
          params.date = this.rankDate
        }
        
        const res = await removeUnitRankUserReq(params)

        if (res.includes('success') || res.includes('成功')) {
          this.$message.success('用户移除成功')
          this.removeUserId = ''
          this.fetchRankData() // 刷新榜单数据
        } else {
          this.$message.error('用户移除失败: ' + res)
        }
      } catch (error) {
        this.$message.error('移除用户失败')
      }
    },
    async handleRankSettlement() {
      if (!this.selectedUnitId) {
        this.$message.warning('请先选择榜单组件')
        return
      }

      if (!this.settlementKey.trim()) {
        this.$message.warning('请输入结算密钥')
        return
      }

      this.$confirm('榜单结算是高危操作，确定要执行吗？', '确认结算', {
        confirmButtonText: '确定结算',
        cancelButtonText: '取消',
        type: 'error'
      }).then(async () => {
        try {
          const params = {
            eventCode: this.eventData.eventCode,
            unitId: this.selectedUnitId,
            settlementKey: this.settlementKey
          }
          
          // date 是可选参数，如果有值才传
          if (this.rankDate) {
            params.date = this.rankDate
          }
          
          const res = await unitRankSettlementReq(params)

          if (res.code === 0 && res.data === true) {
            this.$message.success('榜单结算成功')
            this.settlementKey = ''
            this.fetchRankData() // 刷新榜单数据
          } else {
            this.$message.error('榜单结算失败: ' + (res.msg || '未知错误'))
          }
        } catch (error) {
          this.$message.error('榜单结算失败')
        }
      })
    },
    resetData() {
      this.selectedUnitId = null
      this.topCount = 10
      this.rankDate = null
      this.removeUserId = ''
      this.settlementKey = ''
      this.rankData = []
      this.rankUnits = []
    },
    handleClose() {
      this.dialogVisible = false
    }
  }
}
</script>

<style scoped lang="scss">
.rank-dialog-content {
  .rank-selector {
    margin-bottom: 20px;
    padding: 16px;
    background-color: #f5f7fa;
    border-radius: 4px;
  }

  .operation-area {
    margin-bottom: 20px;
    padding: 16px;
    border: 1px solid #e4e7ed;
    border-radius: 4px;

    .operation-buttons {
      margin-top: 12px;
    }
  }

  .rank-table {
    margin-bottom: 20px;
  }

  .empty-data {
    text-align: center;
    padding: 40px;
  }

  .loading-area {
    position: relative;
    height: 200px;
    display: flex;
    justify-content: center;
    align-items: center;
    
    .loading-content {
      text-align: center;
      color: #909399;
      
      i {
        font-size: 32px;
        animation: rotate 1s linear infinite;
        display: block;
        margin-bottom: 12px;
      }
      
      p {
        margin: 0;
        font-size: 14px;
      }
    }
  }
  
  @keyframes rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
}

.rank-table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  
  .table-info {
    flex: 1;
  }
  
  .table-actions {
    display: flex;
    gap: 12px;
    align-items: center;
  }
}

.rank-pagination {
  margin-top: 20px;
  text-align: center;
}

/* 榜单表格样式 */
::v-deep .el-table {
  .top-rank {
    background-color: #fff7e6;
    
    &:hover {
      background-color: #ffefd6 !important;
    }
  }
}

.dialog-footer {
  text-align: right;
}
</style> 